// HAPPYCAP_CHAT_DESKTOP_LAYOUT_V1
import { useEffect, useState } from "react";
import { getChatFriends } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import { useWebSocketActions } from "../../context/WebSocketContext";
import { useNotification } from "../../context/NotificationContext"; // 👈 NUEVO
import { useTranslation } from "react-i18next";

const FriendAvatar = ({ friend }) => {
  const [imageError, setImageError] = useState(false);

  const nombre = `${friend.nombre || ""} ${friend.apellidos || ""}`.trim();

  const iniciales = nombre
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join("") || "?";

  const foto = friend.photo;

  useEffect(() => {
    setImageError(false);
  }, [foto]);

  if (foto && !imageError) {
    return (
      <img
        src={foto}
        alt={`Foto de ${nombre}`}
        className="h-10 w-10 shrink-0 rounded-full border border-slate-200 bg-slate-100 object-cover"
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#dcecf5] text-sm font-bold text-[#245580]">
      {iniciales}
    </div>
  );
};

const Sidebar = ({ onSelectFriend, selectedFriend }) => {
  const [friends, setFriends] = useState([]);
  const { user } = useAuth();
  const { addListener } = useWebSocketActions();
  const { t } = useTranslation();
  const { unreadBySender } = useNotification(); // 👈 NUEVO

  useEffect(() => {
    const interval = setInterval(() => {
      getChatFriends().then(res => setFriends(res.data)).catch(console.error);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const res = await getChatFriends();
        setFriends(res.data);
      } catch (err) {
        console.error("Error al cargar amigos", err);
      }
    };
    fetchFriends();
  }, []);

  useEffect(() => {
    const removeListener = addListener((data) => {
      if (data?.type === "status_update") {
        setFriends((prev) =>
          prev.map((f) =>
            f.id === data.user_id ? { ...f, online: data.online } : f
          )
        );
      }
    });
    return () => removeListener();
  }, [addListener]);

  const getUnreadCount = (friendId) =>
    unreadBySender.find((u) => Number(u.sender_id) === Number(friendId))?.count || 0;

  const orderedFriends = [...friends].sort((a, b) => {
    if (a.online && !b.online) return -1;
    if (!a.online && b.online) return 1;
    const unreadA = getUnreadCount(a.id);
    const unreadB = getUnreadCount(b.id);
    return unreadB - unreadA;
  });

  // HAPPYCAP_SIDEBAR_WHATSAPP_V2
  const formatActivity = (friend) => {
    const value =
      friend.last_seen ||
      friend.last_activity ||
      friend.ultima_actividad ||
      friend.last_active ||
      null;

    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();

    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    // HAPPYCAP_CHAT_LAST_SEEN_FULL_DATE_V1
    // Para cualquier día distinto de hoy: dd/mm/aaaa.
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  };

  return (
    <aside className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden border-r border-slate-200 bg-white md:w-[380px] md:shrink-0 xl:w-[440px]">

      {/* Título oculto en móvil */}
      <div className="hidden shrink-0 border-b border-slate-100 px-5 py-4 md:block">
        <h2 className="text-lg font-bold text-[#245580]">
          {t("chat.title", "Mensajes")}
        </h2>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {orderedFriends.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">
            {t("chat.no_friends")}
          </div>
        ) : (
          orderedFriends.map((friend) => {
            const isSelected = selectedFriend?.id === friend.id;
            const unread = getUnreadCount(friend.id);
            const lastActivity = formatActivity(friend);

            return (
              <button
                key={friend.id}
                type="button"
                onClick={() => onSelectFriend(friend)}
                className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition hover:bg-[#f2f8fc] ${
                  isSelected ? "bg-[#eaf4fa]" : "bg-white"
                }`}
              >
                <div className="relative shrink-0">
                  <FriendAvatar friend={friend} />

                  <span
                    className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white ${
                      friend.online ? "bg-[#16A34A]" : "bg-red-500"
                    }`}
                    title={friend.online ? "En línea" : "Desconectado"}
                    aria-label={friend.online ? "En línea" : "Desconectado"}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={`truncate text-[14px] text-slate-800 ${
                        unread > 0 ? "font-bold" : "font-semibold"
                      }`}
                    >
                      {friend.nombre} {friend.apellidos}
                    </span>

                    {lastActivity && (
                      <time
                        className="ml-auto shrink-0 whitespace-nowrap text-right text-[11px] font-medium text-slate-500"
                        title="Última actividad"
                      >
                        {lastActivity}
                      </time>
                    )}
                  </div>

                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="min-w-0 flex-1" />

                    {unread > 0 && (
                      <span
                        className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[#16A34A] px-1.5 text-[11px] font-bold text-white"
                        aria-label={`${unread} mensajes sin leer`}
                      >
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
